import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Wind, Anchor, Compass, Zap, Loader2, Sparkles, ArrowRight, Battery, Brain, Play, Pause, Check } from "lucide-react";
import { api } from "@/lib/api";
import { CinematicVideo } from "@/components/CinematicVideo";

const THERMO_VIDEO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/5gbtj40v_gemini_generated_video_a99c3039.mp4";

const BREATH_PHASES = [
  { key: "inhale", sec: 4, scale: 1.55 },
  { key: "hold", sec: 7, scale: 1.55 },
  { key: "exhale", sec: 8, scale: 0.8 },
];

function Breather({ c, onClose }) {
  const [running, setRunning] = useState(true);
  const [pi, setPi] = useState(0);
  const [left, setLeft] = useState(BREATH_PHASES[0].sec);
  const [cycles, setCycles] = useState(0);
  const phase = BREATH_PHASES[pi];

  // advance to next phase after its duration
  useEffect(() => {
    if (!running) return;
    setLeft(phase.sec);
    const to = setTimeout(() => {
      setPi((p) => {
        const next = (p + 1) % BREATH_PHASES.length;
        if (next === 0) setCycles((n) => n + 1);
        return next;
      });
    }, phase.sec * 1000);
    return () => clearTimeout(to);
  }, [pi, running]); // eslint-disable-line

  // 1s countdown display
  useEffect(() => {
    if (!running) return;
    const iv = setInterval(() => setLeft((l) => (l > 1 ? l - 1 : l)), 1000);
    return () => clearInterval(iv);
  }, [pi, running]);

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.4 }} className="overflow-hidden" data-testid="thermometre-breathe-panel">
      <div className="mt-5 rounded-2xl border border-primary/25 bg-background/40 py-10 px-6 flex flex-col items-center">
        <p className="text-sm text-foreground/60 text-center mb-8 max-w-sm">{c.breatheSub}</p>
        <div className="relative flex items-center justify-center" style={{ width: 240, height: 240 }}>
          <div aria-hidden className="absolute rounded-full" style={{ width: 240, height: 240, background: "radial-gradient(circle, hsl(28 96% 56% / 0.10), transparent 70%)" }} />
          <motion.div
            animate={running ? { scale: phase.scale } : {}}
            transition={{ duration: running ? phase.sec : 0, ease: "easeInOut" }}
            className="rounded-full flex items-center justify-center"
            style={{ width: 120, height: 120, background: "radial-gradient(circle at 35% 30%, hsl(44 96% 68% / 0.85), hsl(28 88% 48% / 0.65))", boxShadow: "0 0 60px -6px hsl(32 96% 56% / 0.6)" }}
          >
            <div className="text-center text-[hsl(22,60%,14%)]">
              <p className="font-display text-lg leading-none" data-testid="thermometre-breathe-phase">{c.ph[phase.key]}</p>
              <p className="text-2xl font-semibold tabular-nums mt-1">{left}</p>
            </div>
          </motion.div>
        </div>
        <p className="mt-8 text-xs text-foreground/45 tabular-nums" data-testid="thermometre-breathe-cycles">{cycles} {c.cycles}</p>
        <div className="mt-5 flex flex-wrap items-center justify-center gap-3 w-full">
          <button onClick={() => setRunning((r) => !r)} data-testid="thermometre-breathe-toggle"
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-primary/40 text-primary text-sm hover:bg-primary/10 transition-colors">
            {running ? <Pause size={15} /> : <Play size={15} />} {running ? c.pause : c.resume}
          </button>
          <button onClick={onClose} data-testid="thermometre-breathe-done"
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-white/15 text-foreground/70 text-sm hover:text-primary hover:border-primary/40 transition-colors">
            <Check size={15} /> {c.done}
          </button>
        </div>
      </div>
    </motion.div>
  );
}

const NEEDS = [
  { id: "decompression", icon: Wind },
  { id: "ancrage", icon: Anchor },
  { id: "clarte", icon: Compass },
  { id: "action", icon: Zap },
];

// besoin -> 2 ressources du site (clé + cible de défilement / route)
const RES_MAP = {
  decompression: [["terrier", "anchor-terrier"], ["capsules", "anchor-capsules"]],
  ancrage: [["scribe", "anchor-scribe"], ["rituel", "/atelier"]],
  clarte: [["aiguillon", "anchor-aiguillon"], ["cercle", "anchor-labo"]],
  action: [["treasure", "anchor-treasure"], ["arene", "arene"]],
};

const T = {
  fr: {
    eyebrow: "30 secondes pour toi",
    title: "Le Thermomètre du Mental",
    subtitle: "Ajuste où tu en es, et reçois un cocon de réponse sur-mesure : un micro-exercice guidé et deux ressources adaptées à ton état.",
    energy: "Ton énergie",
    energyLow: "Épuisé", energyHigh: "Énergique",
    load: "Ta charge mentale",
    loadLow: "Surchargé", loadHigh: "Calme",
    needLabel: "De quoi as-tu besoin, là, maintenant ?",
    needs: { decompression: "Décompression", ancrage: "Ancrage", clarte: "Clarté", action: "Action" },
    generate: "Ouvrir mon cocon",
    generating: "Ton cocon se tisse…",
    again: "Recommencer",
    resultEyebrow: "Ton cocon de réponse",
    exerciseLabel: "Ton micro-exercice",
    resourcesLabel: "Deux ressources pour toi",
    open: "Découvrir",
    error: "Le cocon n'a pas pu se tisser. Réessaie.",
    breathe: "Respirer avec moi",
    breatheSub: "Suis le cercle : inspire, retiens, expire — au rythme 4-7-8.",
    ph: { inhale: "Inspire", hold: "Retiens", exhale: "Expire" },
    pause: "Pause", resume: "Reprendre", done: "Terminer", cycles: "cycles",
    whyTitle: "Pourquoi respirer ?",
    whyIntro: "La respiration est la seule fonction de notre système nerveux que l'on peut piloter consciemment. Quelques cycles suffisent à changer d'état — pour chacun de nous, à tout âge.",
    whyPoints: [
      "Apaiser sur-le-champ : ralentir le souffle active le nerf vague, fait baisser le stress et calme l'anxiété.",
      "Retrouver sa concentration : deux minutes de souffle restaurent la clarté et la disponibilité mentale.",
      "Un outil pour la vie : gratuit, sans matériel, toujours avec toi.",
    ],
    breatheNow: "Respirer maintenant",
    res: {
      terrier: { title: "Le Terrier du lapin", desc: "Laisse ta curiosité vagabonder de découverte en découverte." },
      capsules: { title: "Capsules de curiosité", desc: "De courtes bulles de savoir, légères et apaisantes." },
      scribe: { title: "Le Scribe", desc: "Pose tes pensées sur le papier et laisse-les s'éclaircir." },
      rituel: { title: "Le Rituel du matin", desc: "Une petite ancre quotidienne pour bien commencer." },
      aiguillon: { title: "L'Aiguillon", desc: "Dix questions pour y voir clair et penser plus loin." },
      cercle: { title: "Cercle de réflexion", desc: "Explore une idée sous tous ses angles, en douceur." },
      treasure: { title: "La Chasse au trésor", desc: "Résous l'énigme du jour et déverrouille une pépite." },
      arene: { title: "L'Arène", desc: "Prends position dans un débat et fais entendre ta voix." },
    },
  },
  en: {
    eyebrow: "30 seconds for you",
    title: "The Mind Thermometer",
    subtitle: "Set where you are and get a tailor-made cocoon: a short guided exercise and two resources matched to your state.",
    energy: "Your energy",
    energyLow: "Drained", energyHigh: "Energised",
    load: "Your mental load",
    loadLow: "Overloaded", loadHigh: "Calm",
    needLabel: "What do you need, right now?",
    needs: { decompression: "Decompress", ancrage: "Grounding", clarte: "Clarity", action: "Action" },
    generate: "Open my cocoon",
    generating: "Weaving your cocoon…",
    again: "Start over",
    resultEyebrow: "Your response cocoon",
    exerciseLabel: "Your micro-exercise",
    resourcesLabel: "Two resources for you",
    open: "Discover",
    error: "The cocoon couldn't be woven. Try again.",
    breathe: "Breathe with me",
    breatheSub: "Follow the circle: inhale, hold, exhale — on the 4-7-8 rhythm.",
    ph: { inhale: "Inhale", hold: "Hold", exhale: "Exhale" },
    pause: "Pause", resume: "Resume", done: "Done", cycles: "cycles",
    whyTitle: "Why breathe?",
    whyIntro: "Breathing is the only function of our nervous system we can steer consciously. A few cycles are enough to shift your state — for each of us, at any age.",
    whyPoints: [
      "Calm on the spot: slowing the breath activates the vagus nerve, lowers stress and eases anxiety.",
      "Refocus: two minutes of breathing restore mental clarity and availability.",
      "A tool for life: free, no equipment, always with you.",
    ],
    breatheNow: "Breathe now",
    res: {
      terrier: { title: "The Rabbit Hole", desc: "Let your curiosity wander from one discovery to the next." },
      capsules: { title: "Curiosity capsules", desc: "Short, light bubbles of knowledge to soothe the mind." },
      scribe: { title: "The Scribe", desc: "Put your thoughts on paper and let them clear up." },
      rituel: { title: "The Morning Ritual", desc: "A small daily anchor to start well." },
      aiguillon: { title: "The Goad", desc: "Ten questions to see clearly and think further." },
      cercle: { title: "Reflection Circle", desc: "Explore one idea from every angle, gently." },
      treasure: { title: "The Treasure Hunt", desc: "Solve today's riddle and unlock a gem." },
      arene: { title: "The Arena", desc: "Take a stand in a debate and make your voice heard." },
    },
  },
  es: {
    eyebrow: "30 segundos para ti",
    title: "El Termómetro Mental",
    subtitle: "Ajusta cómo estás y recibe un capullo a medida: un micro-ejercicio guiado y dos recursos adaptados a tu estado.",
    energy: "Tu energía",
    energyLow: "Agotado", energyHigh: "Enérgico",
    load: "Tu carga mental",
    loadLow: "Saturado", loadHigh: "En calma",
    needLabel: "¿Qué necesitas, ahora mismo?",
    needs: { decompression: "Descompresión", ancrage: "Anclaje", clarte: "Claridad", action: "Acción" },
    generate: "Abrir mi capullo",
    generating: "Tejiendo tu capullo…",
    again: "Empezar de nuevo",
    resultEyebrow: "Tu capullo de respuesta",
    exerciseLabel: "Tu micro-ejercicio",
    resourcesLabel: "Dos recursos para ti",
    open: "Descubrir",
    error: "El capullo no se pudo tejer. Inténtalo de nuevo.",
    breathe: "Respira conmigo",
    breatheSub: "Sigue el círculo: inhala, retén, exhala — al ritmo 4-7-8.",
    ph: { inhale: "Inhala", hold: "Retén", exhale: "Exhala" },
    pause: "Pausa", resume: "Reanudar", done: "Terminar", cycles: "ciclos",
    whyTitle: "¿Por qué respirar?",
    whyIntro: "La respiración es la única función de nuestro sistema nervioso que podemos dirigir conscientemente. Unos pocos ciclos bastan para cambiar de estado — para cada uno de nosotros, a cualquier edad.",
    whyPoints: [
      "Calmar al instante: ralentizar la respiración activa el nervio vago, baja el estrés y alivia la ansiedad.",
      "Reconcentrarse: dos minutos de respiración restauran la claridad y la disponibilidad mental.",
      "Una herramienta para la vida: gratis, sin material, siempre contigo.",
    ],
    breatheNow: "Respirar ahora",
    res: {
      terrier: { title: "La Madriguera", desc: "Deja que tu curiosidad vague de descubrimiento en descubrimiento." },
      capsules: { title: "Cápsulas de curiosidad", desc: "Pequeñas burbujas de saber, ligeras y calmantes." },
      scribe: { title: "El Escriba", desc: "Pon tus pensamientos en papel y deja que se aclaren." },
      rituel: { title: "El Ritual matutino", desc: "Un pequeño anclaje diario para empezar bien." },
      aiguillon: { title: "El Aguijón", desc: "Diez preguntas para ver claro y pensar más lejos." },
      cercle: { title: "Círculo de reflexión", desc: "Explora una idea desde todos los ángulos, con suavidad." },
      treasure: { title: "La Búsqueda del tesoro", desc: "Resuelve el enigma del día y desbloquea una joya." },
      arene: { title: "La Arena", desc: "Toma posición en un debate y haz oír tu voz." },
    },
  },
  nl: {
    eyebrow: "30 seconden voor jou",
    title: "De Mentale Thermometer",
    subtitle: "Stel in hoe je erbij zit en ontvang een cocon op maat: een korte begeleide oefening en twee bronnen afgestemd op jouw toestand.",
    energy: "Je energie",
    energyLow: "Uitgeput", energyHigh: "Energiek",
    load: "Je mentale belasting",
    loadLow: "Overladen", loadHigh: "Kalm",
    needLabel: "Wat heb je nu nodig?",
    needs: { decompression: "Ontspanning", ancrage: "Verankering", clarte: "Helderheid", action: "Actie" },
    generate: "Open mijn cocon",
    generating: "Je cocon wordt geweven…",
    again: "Opnieuw",
    resultEyebrow: "Jouw antwoord-cocon",
    exerciseLabel: "Je micro-oefening",
    resourcesLabel: "Twee bronnen voor jou",
    open: "Ontdekken",
    error: "De cocon kon niet worden geweven. Probeer opnieuw.",
    breathe: "Adem met mij",
    breatheSub: "Volg de cirkel: adem in, houd vast, adem uit — op het 4-7-8 ritme.",
    ph: { inhale: "Adem in", hold: "Houd vast", exhale: "Adem uit" },
    pause: "Pauze", resume: "Hervat", done: "Klaar", cycles: "cycli",
    whyTitle: "Waarom ademen?",
    whyIntro: "Ademhaling is de enige functie van ons zenuwstelsel die we bewust kunnen sturen. Een paar cycli volstaan om van toestand te veranderen — voor ieder van ons, op elke leeftijd.",
    whyPoints: [
      "Meteen kalmeren: trager ademen activeert de nervus vagus, verlaagt stress en verzacht angst.",
      "Herfocussen: twee minuten ademhaling herstellen helderheid en mentale beschikbaarheid.",
      "Een tool voor het leven: gratis, zonder materiaal, altijd bij je.",
    ],
    breatheNow: "Nu ademen",
    res: {
      terrier: { title: "Het Konijnenhol", desc: "Laat je nieuwsgierigheid van ontdekking naar ontdekking dwalen." },
      capsules: { title: "Nieuwsgierigheidscapsules", desc: "Korte, lichte bubbels kennis om de geest te kalmeren." },
      scribe: { title: "De Schrijver", desc: "Zet je gedachten op papier en laat ze helder worden." },
      rituel: { title: "Het Ochtendritueel", desc: "Een klein dagelijks anker om goed te starten." },
      aiguillon: { title: "De Prikkel", desc: "Tien vragen om helder te zien en verder te denken." },
      cercle: { title: "Reflectiecirkel", desc: "Verken één idee vanuit alle hoeken, rustig." },
      treasure: { title: "De Schattenjacht", desc: "Los het raadsel van de dag op en ontgrendel een pareltje." },
      arene: { title: "De Arena", desc: "Neem een standpunt in een debat en laat je stem horen." },
    },
  },
};

export function ThermometreMental() {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "fr").slice(0, 2);
  const c = T[lang] || T.fr;
  const [energy, setEnergy] = useState(50);
  const [load, setLoad] = useState(50);
  const [need, setNeed] = useState("decompression");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [breathing, setBreathing] = useState(false);
  const [standaloneBreath, setStandaloneBreath] = useState(false);
  const [showWhy, setShowWhy] = useState(false);

  const generate = async () => {
    setLoading(true); setError(""); setResult(null); setBreathing(false);
    try {
      const { data } = await api.post("/public/thermometre", { energy, mental_load: load, need, lang });
      setResult(data);
    } catch {
      setError(c.error);
    } finally {
      setLoading(false);
    }
  };

  const goTo = (target) => {
    if (target.startsWith("/")) { window.location.href = target; return; }
    const el = document.getElementById(target);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const sliderStyle = { accentColor: "hsl(28 96% 56%)" };
  const resNeed = (result && result.need) || need;
  const resources = RES_MAP[resNeed] || RES_MAP.decompression;

  return (
    <section className="relative px-6 my-16 sm:my-20" data-testid="thermometre-section">
      <div className="max-w-4xl mx-auto relative rounded-[2rem] overflow-hidden border border-primary/20 frame-pulse glass">
        <div aria-hidden className="amber-glow" style={{ top: "-20%", left: "50%", transform: "translateX(-50%)", width: "620px", height: "460px" }} />
        <div className="relative z-10 px-6 sm:px-12 py-14">
          <div className="text-center">
            <p className="uppercase tracking-[0.28em] text-xs text-primary mb-3" data-testid="thermometre-eyebrow">{c.eyebrow}</p>
            <h2 className="font-display text-gradient-orange text-4xl sm:text-6xl leading-[1.12] pb-[0.08em] mb-4" data-testid="thermometre-title">{c.title}</h2>
            <p className="text-sm sm:text-base text-foreground/60 max-w-2xl mx-auto">{c.subtitle}</p>
          </div>

          <div className="mt-8 max-w-xl mx-auto">
            <CinematicVideo fade gold src={THERMO_VIDEO} testId="thermometre-cinematic-video" />
          </div>

          {/* Curseurs */}
          <div className="mt-10 space-y-8 max-w-xl mx-auto">
            <div data-testid="thermometre-energy">
              <div className="flex items-center gap-2 mb-2 text-foreground/80">
                <Battery size={16} className="text-primary" />
                <span className="text-sm font-medium">{c.energy}</span>
                <span className="ml-auto text-xs text-primary tabular-nums">{energy}</span>
              </div>
              <input type="range" min="0" max="100" value={energy} style={sliderStyle}
                onChange={(ev) => setEnergy(Number(ev.target.value))}
                data-testid="thermometre-energy-slider"
                className="w-full h-2 rounded-full bg-white/10 appearance-none cursor-pointer" />
              <div className="flex justify-between text-[11px] text-foreground/40 mt-1.5">
                <span>{c.energyLow}</span><span>{c.energyHigh}</span>
              </div>
            </div>

            <div data-testid="thermometre-load">
              <div className="flex items-center gap-2 mb-2 text-foreground/80">
                <Brain size={16} className="text-primary" />
                <span className="text-sm font-medium">{c.load}</span>
                <span className="ml-auto text-xs text-primary tabular-nums">{load}</span>
              </div>
              <input type="range" min="0" max="100" value={load} style={sliderStyle}
                onChange={(ev) => setLoad(Number(ev.target.value))}
                data-testid="thermometre-load-slider"
                className="w-full h-2 rounded-full bg-white/10 appearance-none cursor-pointer" />
              <div className="flex justify-between text-[11px] text-foreground/40 mt-1.5">
                <span>{c.loadLow}</span><span>{c.loadHigh}</span>
              </div>
            </div>

            {/* Besoin immédiat */}
            <div data-testid="thermometre-needs">
              <p className="text-sm font-medium text-foreground/80 mb-3 text-center">{c.needLabel}</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                {NEEDS.map((n) => {
                  const Icon = n.icon;
                  const on = need === n.id;
                  return (
                    <button key={n.id} onClick={() => setNeed(n.id)}
                      data-testid={`thermometre-need-${n.id}`}
                      className={`flex flex-col items-center gap-2 px-3 py-4 rounded-xl border transition-colors ${on ? "border-primary/60 bg-primary/15 text-primary" : "border-primary/25 text-foreground/60 hover:bg-primary/10 hover:border-primary/50"}`}>
                      <Icon size={20} />
                      <span className="text-xs">{c.needs[n.id]}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="text-center pt-2">
              <button onClick={generate} disabled={loading}
                data-testid="thermometre-generate-btn"
                className="btn-frost-orange inline-flex items-center gap-2 px-8 py-3.5 rounded-full font-semibold text-sm disabled:opacity-60">
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                {loading ? c.generating : c.generate}
              </button>
              {error && <p className="mt-3 text-sm text-red-300/80" data-testid="thermometre-error">{error}</p>}
            </div>

            {/* Pourquoi respirer ? + respiration autonome */}
            <div className="pt-6 mt-2 border-t border-white/8 text-center">
              <div className="flex flex-wrap items-center justify-center gap-3">
                <button onClick={() => setStandaloneBreath((v) => !v)} data-testid="thermometre-breathe-now-btn"
                  className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-full border border-primary/40 text-primary text-sm font-medium hover:bg-primary/10 transition-colors">
                  <Wind size={15} /> {c.breatheNow}
                </button>
                <button onClick={() => setShowWhy((v) => !v)} data-testid="thermometre-why-btn"
                  className="text-sm text-foreground/55 hover:text-primary transition-colors underline underline-offset-4">
                  {c.whyTitle}
                </button>
              </div>
              <AnimatePresence>
                {showWhy && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.4 }} className="overflow-hidden" data-testid="thermometre-why">
                    <div className="mt-5 rounded-2xl border border-white/10 bg-white/[0.03] p-6 text-left">
                      <p className="text-sm text-foreground/70 italic leading-relaxed mb-4">{c.whyIntro}</p>
                      <ul className="space-y-2.5">
                        {c.whyPoints.map((p, i) => (
                          <li key={i} className="flex items-start gap-2.5 text-sm text-foreground/65 leading-relaxed">
                            <Wind size={14} className="text-primary mt-1 flex-shrink-0" />
                            <span>{p}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              <AnimatePresence>
                {standaloneBreath && <Breather c={c} onClose={() => setStandaloneBreath(false)} />}
              </AnimatePresence>
            </div>
          </div>

          {/* Cocon de réponse */}
          <AnimatePresence>
            {result && (
              <motion.div
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                transition={{ duration: 0.6 }}
                className="mt-10 max-w-2xl mx-auto" data-testid="thermometre-result">
                <p className="text-center uppercase tracking-[0.24em] text-[11px] text-primary/80 mb-3">{c.resultEyebrow}</p>
                <p className="text-center font-display italic text-lg sm:text-xl text-foreground/90 leading-relaxed mb-8" data-testid="thermometre-greeting">
                  {result.greeting}
                </p>

                {/* Exercice */}
                <div className="rounded-2xl border border-primary/25 bg-primary/[0.05] p-6" data-testid="thermometre-exercise">
                  <div className="flex items-center gap-2.5 mb-1">
                    <span className="w-8 h-8 rounded-lg bg-primary/15 border border-primary/30 flex items-center justify-center text-primary flex-shrink-0"><Wind size={16} /></span>
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-primary/70">{c.exerciseLabel}</p>
                      <h3 className="font-display text-lg text-foreground/90">{result.exercise.name}</h3>
                    </div>
                    <span className="ml-auto text-xs text-primary/80 border border-primary/30 rounded-full px-3 py-1">{result.exercise.duration}</span>
                  </div>
                  {result.exercise.intro && <p className="text-sm text-foreground/60 italic mt-3 mb-4">{result.exercise.intro}</p>}
                  <ol className="space-y-3 mt-2">
                    {result.exercise.steps.map((s, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-foreground/75 leading-relaxed">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/12 border border-primary/25 text-primary text-xs flex items-center justify-center tabular-nums mt-0.5">{i + 1}</span>
                        <span>{s}</span>
                      </li>
                    ))}
                  </ol>
                  <div className="mt-5 pt-4 border-t border-primary/15 flex justify-center">
                    {!breathing ? (
                      <button onClick={() => setBreathing(true)} data-testid="thermometre-breathe-btn"
                        className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full border border-primary/40 text-primary text-sm font-medium hover:bg-primary/10 transition-colors">
                        <Wind size={15} /> {c.breathe}
                      </button>
                    ) : null}
                  </div>
                  <AnimatePresence>
                    {breathing && <Breather c={c} onClose={() => setBreathing(false)} />}
                  </AnimatePresence>
                </div>

                {/* Ressources */}
                <p className="mt-8 text-center text-sm font-medium text-foreground/70 mb-3">{c.resourcesLabel}</p>
                <div className="grid sm:grid-cols-2 gap-3" data-testid="thermometre-resources">
                  {resources.map(([key, target]) => (
                    <button key={key} onClick={() => goTo(target)}
                      data-testid={`thermometre-resource-${key}`}
                      className="text-left rounded-2xl border border-white/12 bg-white/[0.03] p-5 hover:border-primary/45 hover:bg-primary/[0.06] transition-colors group">
                      <h4 className="font-display text-base text-foreground/90 mb-1">{c.res[key].title}</h4>
                      <p className="text-xs text-foreground/55 leading-relaxed mb-3">{c.res[key].desc}</p>
                      <span className="inline-flex items-center gap-1.5 text-xs text-primary group-hover:gap-2.5 transition-all">
                        {c.open} <ArrowRight size={13} />
                      </span>
                    </button>
                  ))}
                </div>

                <div className="text-center mt-8">
                  <button onClick={() => { setResult(null); setBreathing(false); }} data-testid="thermometre-again-btn"
                    className="text-sm text-foreground/50 hover:text-primary transition-colors underline underline-offset-4">
                    {c.again}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
